import React, { HTMLAttributes } from 'react';

import { cn } from '@/lib/utils/cn';
import moment from 'moment-jalaali';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip';

interface ProgressBarItems {
  value: number;
  color: string;
  title: React.ReactNode;
  date?: React.ReactNode;
}

interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  totalValue: number;
  progressItems: ProgressBarItems[];
  definitionDate: string;
  underwritingDate: string;
  underwritingEndDate: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  totalValue,
  progressItems,
  definitionDate,
  underwritingDate,
  underwritingEndDate,
  className,
  ...props
}) => {
  const calculateWidthPercent = (value: number) => {
    const percentage = (value / totalValue) * 100;
    return percentage > 1 ? percentage : 1;
  };

  const calculateWidth = (value: number) => `${calculateWidthPercent(value)}%`;

  // compare calendar days, not exact timestamps, so "today" lines up flush with a date that's the same day
  const toDay = (date: string) => moment(date).startOf('day');
  const definitionDay = toDay(definitionDate);
  const underwritingDay = toDay(underwritingDate);
  const underwritingEndDay = toDay(underwritingEndDate);
  const today = moment().startOf('day');

  const preOrderValue = underwritingDay.diff(definitionDay);
  const underwritingValue = underwritingEndDay.diff(underwritingDay);
  // where underwritingDate sits on the bar, matching the underwriting segment's own rendered width
  const boundaryPercent = calculateWidthPercent(underwritingValue);

  const showMarker = !today.isBefore(definitionDay) && !today.isAfter(underwritingEndDay);

  let positionPercent = 0;
  if (today.isAfter(underwritingEndDay)) {
    positionPercent = 0;
  } else if (today.isBefore(underwritingDay)) {
    // today is between definitionDate and underwritingDate
    const fraction = preOrderValue > 0 ? today.diff(definitionDay) / preOrderValue : 0;
    positionPercent = 100 - fraction * (100 - boundaryPercent);
  } else {
    // today is between underwritingDate and underwritingEndDate
    const fraction = underwritingValue > 0 ? today.diff(underwritingDay) / underwritingValue : 0;
    positionPercent = boundaryPercent * (1 - fraction);
  }

  const markerWidth = 5;
  const currentPositionStyle = `calc((100% - ${markerWidth}px) * ${positionPercent / 100})`;

  return (
    <div className={cn(`flex flex-col gap-2 pt-4 ${className}`)} {...props}>
      <ul className='flex list-none gap-1 items-center flex-row-reverse'>
        {progressItems.map((item) => (
          <li
            key={item.value}
            style={{
              fontSize: '12px',
              lineHeight: '20.73px',
              fontWeight: 'normal',
              color: `${item.color}`,
              width: `${calculateWidth(item.value)}`,
              minWidth: 130,
            }}
          >
            {item.title}
          </li>
        ))}
      </ul>
      <div className='relative w-full'>
        <ul className='flex list-none gap-1 items-center flex-row-reverse opacity-[0.8] w-full '>
          {progressItems.map((item, index) => (
            <li
              className={
                index === 0
                  ? 'rounded-tl-md rounded-bl-md'
                  : index === progressItems.length - 1
                    ? 'rounded-br-md rounded-tr-md'
                    : ''
              }
              key={item.value}
              style={{
                height: '8px',
                backgroundColor: `${item.color}`,
                width: `${calculateWidth(item.value)}`,
              }}
            ></li>
          ))}
        </ul>
        {showMarker && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className='absolute bg-text rounded-sm cursor-pointer'
                  style={{
                    left: currentPositionStyle,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '14px',
                    width: `${markerWidth}px`,
                  }}
                ></div>
              </TooltipTrigger>
              <TooltipContent
                onPointerDownOutside={(event) => {
                  event.preventDefault();
                }}
              >
                <p>{moment().format('jYYYY/jMM/jDD')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <ul className='flex list-none gap-1 items-center flex-row-reverse'>
        {progressItems.map((item) => (
          <li
            key={item.value}
            style={{
              fontSize: '12px',
              lineHeight: '20.73px',
              fontWeight: 'normal',
              color: `${item.color}`,
              width: `${calculateWidth(item.value)}`,
              minWidth: 130,
            }}
          >
            {item.date}
          </li>
        ))}
      </ul>
    </div>
  );
};

export { ProgressBar };
export type { ProgressBarItems, ProgressBarProps };
